import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button, Icon, SideNavBar, TopAppBar } from '@ng-unifi/ui';
import { useAuth } from '../auth/AuthContext.js';
import { useTenantScope } from '../auth/TenantScopeContext.js';
import { BottomNav, type BottomNavItem } from '../components/BottomNav.js';
import { NavDrawer } from '../components/NavDrawer.js';
import { isNavItemActive } from '../lib/navActive.js';
import { RouteErrorBoundary } from '../components/RouteErrorBoundary.js';

export { isNavItemActive };
import { ThemeToggle } from '../components/ThemeToggle.js';

// Navigation is role- and mode-shaped, in named groups.
//
// UX-002: every item used to be visible to every authenticated role unless it
// opted out, so a resident signing in saw Dashboard, Buildings, Residents,
// Activity, Access Zones and Schedules — six operator surfaces, all of which
// fail server-side. The default is now the other way round: an item declares
// who may see it.
//
// UX-029: 23 routes had no menu entry and no in-app link, including every
// administration and recovery surface. Grouping is what lets them all have an
// entry point without a flat 21-item list.
type SiteMode = 'commercial_multi_tenant' | 'single_tenant' | 'multi_family';
type AuthRole = 'master_admin' | 'tenant_admin' | 'tenant_manager' | 'tenant_viewer' | 'resident';

interface ShellLayoutProps {
  siteMode: SiteMode;
  siteName: string;
}

const OPERATORS: AuthRole[] = ['master_admin', 'tenant_admin', 'tenant_manager', 'tenant_viewer'];
const MANAGERS: AuthRole[] = ['master_admin', 'tenant_admin', 'tenant_manager'];

interface NavItem {
  key: string;
  label: string;
  href: string;
  /** Master-only surface. Implies the route's own RequireMaster guard. */
  masterOnly?: boolean;
  /**
   * Roles that may see this link. Required for anything that is not
   * master-only — an item with neither is a bug, and `navItemsFor` asserts it.
   */
  visibleRoles?: AuthRole[];
  /** Section header. Items sharing a group render under one heading. */
  group?: string;
}

function navItemsFor(mode: SiteMode): NavItem[] {
  const isMultiFamily = mode === 'multi_family';

  // Residents get their own surfaces and nothing else. Every one of these was
  // previously unreachable: /me/home had no outgoing links at all, and the only
  // links to /me/credentials and /me/events lived on a page residents cannot
  // open (UX-001).
  const resident: NavItem[] = [
    { key: 'me-home', label: 'My Home', href: '/me/home', visibleRoles: ['resident'], group: 'My access' },
    {
      key: 'me-credentials',
      label: 'My Keys',
      href: '/me/credentials',
      visibleRoles: ['resident'],
      group: 'My access',
    },
    { key: 'me-events', label: 'My Activity', href: '/me/events', visibleRoles: ['resident'], group: 'My access' },
    ...(isMultiFamily
      ? [
          {
            key: 'me-amenities',
            label: 'Amenity Bookings',
            href: '/me/amenities/reservations',
            visibleRoles: ['resident'] as AuthRole[],
            group: 'My access',
          },
        ]
      : []),
  ];

  const operate: NavItem[] = [
    { key: 'home', label: 'Dashboard', href: '/', visibleRoles: OPERATORS, group: 'Operate' },
    { key: 'activity', label: 'Activity', href: '/activity', visibleRoles: OPERATORS, group: 'Operate' },
    { key: 'journeys', label: 'Journeys', href: '/journeys', visibleRoles: OPERATORS, group: 'Operate' },
    { key: 'hardware', label: 'Hardware', href: '/hardware', masterOnly: true, group: 'Operate' },
    { key: 'outbox', label: 'Sync Queue', href: '/outbox', masterOnly: true, group: 'Operate' },
  ];

  const people: NavItem[] = [
    ...(mode === 'commercial_multi_tenant'
      ? [{ key: 'tenants', label: 'Tenants', href: '/tenants', masterOnly: true, group: 'People' } as NavItem]
      : []),
    isMultiFamily
      ? { key: 'residents', label: 'Residents', href: '/residents', visibleRoles: MANAGERS, group: 'People' }
      : { key: 'employees', label: 'Employees', href: '/employees', visibleRoles: OPERATORS, group: 'People' },
    {
      key: 'admin-vendors',
      label: 'Vendor Access',
      href: '/admin/vendors',
      visibleRoles: ['master_admin', 'tenant_admin'],
      group: 'People',
    },
    { key: 'admin-users', label: 'Users', href: '/admin/users', masterOnly: true, group: 'People' },
  ];

  // Multi-family property management. Buildings and Residents used to be
  // visible to residents themselves; these are operator surfaces.
  const property: NavItem[] = isMultiFamily
    ? [
        // Units and Households left the nav: they are levels *inside* a
        // building, and Buildings is now a real entry point into them
        // (building -> units -> households -> residents). They were siblings
        // here only because BuildingsPage linked nowhere.
        { key: 'buildings', label: 'Buildings', href: '/buildings', visibleRoles: MANAGERS, group: 'Property' },
        { key: 'amenities', label: 'Amenities', href: '/amenities', visibleRoles: MANAGERS, group: 'Property' },
        {
          key: 'amenity-reservations',
          label: 'Amenity Bookings',
          href: '/admin/amenities/reservations',
          visibleRoles: MANAGERS,
          group: 'Property',
        },
        { key: 'guest-passes', label: 'Guest Passes', href: '/guest-passes', visibleRoles: MANAGERS, group: 'Property' },
        { key: 'intercom', label: 'Intercom', href: '/intercom', visibleRoles: MANAGERS, group: 'Property' },
      ]
    : [];

  const access: NavItem[] = [
    { key: 'access-zones', label: 'Access Zones', href: '/access-groups', visibleRoles: OPERATORS, group: 'Access' },
    { key: 'schedules', label: 'Schedules', href: '/schedules', visibleRoles: OPERATORS, group: 'Access' },
    { key: 'credential-enrollment', label: 'Credentials', href: '/credentials/enroll', visibleRoles: MANAGERS, group: 'Access' },
    { key: 'callboxes', label: 'Callboxes', href: '/callbox', visibleRoles: MANAGERS, group: 'Access' },
  ];

  // Twelve rows collapsed to one. Everything configure-once lives behind
  // /admin/settings, which is a hub over the same pages rather than a rewrite
  // of them — see docs/ux-review/NAVIGATION_AUDIT.md. A master_admin was
  // looking at 24-29 nav items; most of them were install-time tasks sitting
  // beside daily operations.
  //
  // The two genuinely operational members of the old group did not go into
  // the hub: Sync Queue is where the dashboard's failed-job alert lands, and
  // Credential Inventory answers "who holds this fob", which is a question
  // people ask during a shift, not during setup.
  const admin: NavItem[] = [
    { key: 'admin-settings', label: 'Settings', href: '/admin/settings', masterOnly: true, group: 'Administration' },
  ];

  const items = [...resident, ...operate, ...people, ...property, ...access, ...admin];
  // An item with neither `masterOnly` nor `visibleRoles` is visible to
  // everyone, which is the exact bug UX-002 describes. Fail loudly in
  // development rather than shipping another one.
  if (import.meta.env?.DEV) {
    const ungated = items.filter((i) => !i.masterOnly && !i.visibleRoles);
    if (ungated.length > 0) {
      throw new Error(
        `Nav items must declare masterOnly or visibleRoles: ${ungated.map((i) => i.key).join(', ')}`,
      );
    }
  }
  return items;
}

export function ShellLayout({ siteMode, siteName }: ShellLayoutProps) {
  const { state, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId } = useTenantScope();
  const isMaster = state.status === 'authed' && state.user.role === 'master_admin';

  // P0-1: default-tenant health banner. `/auth/me` returns
  // `default_tenant_health` ∈ {ok, missing, inactive, unset}. For
  // single_tenant + multi_family installs, anything other than `ok`
  // means the pinned default tenant is broken — every tenant-scoped
  // page will fail until a master repairs it.
  const defaultHealth = state.status === 'authed' ? state.me.default_tenant_health : 'ok';
  const showDefaultHealthBanner =
    state.status === 'authed' &&
    (siteMode === 'single_tenant' || siteMode === 'multi_family') &&
    defaultHealth !== 'ok' &&
    // `unset` is a low-severity legacy-install notice — master-only.
    (defaultHealth !== 'unset' || isMaster);

  const currentRole = state.status === 'authed' ? state.user.role : null;
  const navItems = navItemsFor(siteMode)
    .filter((i) => {
      if (i.masterOnly) return isMaster;
      // UX-002: no `visibleRoles` no longer means "everyone". An item must say
      // who it is for; navItemsFor throws in development if one does not.
      if (!currentRole || !i.visibleRoles) return false;
      return i.visibleRoles.includes(currentRole);
    })
    .map((i) =>
      i.key === 'access-zones' && isMaster && !tenantId ? { ...i, href: '/admin/access-zones' } : i,
    );

  // Mobile drawer state.
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Bottom nav — 4 core items; "More" opens the full drawer.
  const credentialBottomNavItem: BottomNavItem | null =
    currentRole === 'master_admin' ||
    currentRole === 'tenant_admin' ||
    currentRole === 'tenant_manager'
      ? {
          key: 'credentials',
          label: 'Credentials',
          shortLabel: 'Keys',
          icon: 'key',
          href: '/credentials/enroll',
        }
      : null;
  // UX-002: the bottom nav had the same defect as the sidebar — a resident on
  // a phone got Home, Activity and Residents, all of which fail server-side.
  const bottomNavItems: BottomNavItem[] =
    currentRole === 'resident'
      ? [
          { key: 'me-home', label: 'Home', icon: 'home', href: '/me/home' },
          { key: 'me-credentials', label: 'My Keys', shortLabel: 'Keys', icon: 'key', href: '/me/credentials' },
          // Not "Activity": that is the operator audit log, and two different
          // things wearing the same label is how the old nav confused
          // residents in the first place.
          { key: 'me-events', label: 'My Activity', shortLabel: 'History', icon: 'history', href: '/me/events' },
          ...(siteMode === 'multi_family'
            ? [
                {
                  key: 'me-amenities',
                  label: 'Bookings',
                  icon: 'event_available',
                  href: '/me/amenities/reservations',
                },
              ]
            : []),
        ]
      : [
          { key: 'home', label: 'Home', icon: 'home', href: '/' },
          { key: 'activity', label: 'Activity', icon: 'history', href: '/activity' },
          {
            key: 'people',
            label: siteMode === 'multi_family' ? 'Residents' : 'Employees',
            icon: 'group',
            href: siteMode === 'multi_family' ? '/residents' : '/employees',
          },
          ...(credentialBottomNavItem ? [credentialBottomNavItem] : []),
        ];

  return (
    <div className="min-h-dvh bg-background">
      {/* UX-032: `siteName` was accepted and never read, so every install
          rendered the literal "AXN PRO ACCESS" while App.tsx drove
          document.title from site_name — the tab and the header disagreed.
          No code path may hard-code the site (see root CLAUDE.md). */}
      <TopAppBar
        brand={
          <span className="truncate font-headline-sm text-headline-sm">
            {siteName?.trim() || 'Access Portal'}
          </span>
        }
        actions={
          <>
            {/* The email used to be inert text beside a Sign out button. A
                person's own surfaces (workplace, credentials, activity,
                account security) were scattered across the operator nav or
                had no entry point at all; they belong here, under the
                identity they relate to. */}
            {state.status === 'authed' ? (
              <AccountMenu
                email={state.user.email}
                isResident={currentRole === 'resident'}
                isMultiFamily={siteMode === 'multi_family'}
                onNavigate={navigate}
              />
            ) : null}
            {/* The three-way control is 106px. At 360px that is a third of
                the viewport spent on a setting a person changes once, and it
                pushed every page into horizontal scroll. Below `sm` it moves
                into the account menu, which is the same place the email
                already goes. */}
            <span className="hidden sm:inline-flex">
              <ThemeToggle />
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                await signOut();
                navigate('/sign-in', { replace: true });
              }}
            >
              Sign out
            </Button>
          </>
        }
      />
      {showDefaultHealthBanner ? (
        <DefaultTenantHealthBanner
          health={defaultHealth}
          isMaster={isMaster}
          onRepair={() => navigate('/admin/settings/default-tenant')}
        />
      ) : null}
      <div className="flex">
        {/* Sidebar — SideNavBar owns its own sticky/hidden/width layout. */}
        <SideNavBar
          items={navItems.map((i) => ({
            key: i.key,
            label: i.label,
            href: i.href,
            active: isNavItemActive(location.pathname, i.href),
            group: i.group,
          }))}
          onNavigate={(item) => navigate(item.href)}
        />
        {/* Emergency Lockdown + Settings — fixed at bottom of sidebar column.
            Hidden on mobile (md:block) to match the sidebar's visibility.
            UX-003: the lockdown trigger used to render for every role, but
            /admin/lockdown is RequireMaster — a tenant operator or resident
            who pressed it was silently redirected to the dashboard. It stays
            one tap away for the masters who can actually use it. */}
        <div className="hidden md:block fixed bottom-0 left-0 w-56 bg-surface border-t border-outline-variant space-y-1 px-3 py-4 z-10">
          {isMaster ? (
            <button
              type="button"
              onClick={() => navigate('/admin/lockdown')}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold text-error bg-error-container hover:bg-error-container/80"
            >
              ⚠ Emergency Lockdown
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => navigate('/account/security')}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-on-surface-variant hover:text-on-surface"
          >
            <Icon name="shield" className="text-sm" />
            Account security
          </button>
        </div>
        <main className="min-w-0 flex-1 p-margin-mobile md:p-margin-desktop pb-16 md:pb-0">
          {/* Keyed by pathname so navigating away clears a caught error —
              without that, one bad page poisons every subsequent route.
              Inside <main> so the nav survives and there is a way out. */}
          <RouteErrorBoundary key={location.pathname}>
            <Outlet />
          </RouteErrorBoundary>
        </main>
      </div>
      {/* Mobile bottom nav + full-menu drawer */}
      <BottomNav items={bottomNavItems} onMore={() => setDrawerOpen(true)} />
      <NavDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        items={navItems.map((i) => ({ key: i.key, label: i.label, href: i.href, group: i.group }))}
        // UX-003: same gate as the desktop trigger. Undefined hides it.
        onLockdown={
          isMaster
            ? () => {
                setDrawerOpen(false);
                navigate('/admin/lockdown');
              }
            : undefined
        }
      />
    </div>
  );
}

/**
 * The identity menu in the top bar.
 *
 * Deliberately a `<details>` rather than a hand-rolled popover: it is
 * keyboard-operable, Escape-dismissible and screen-reader-announced by the
 * platform, with no focus management to get wrong. The dialogs in this app
 * needed three attempts to get that right (UX-021/022/023); a disclosure
 * widget does not need any.
 */
function AccountMenu({
  email,
  isResident,
  isMultiFamily,
  onNavigate,
}: {
  email: string;
  isResident: boolean;
  isMultiFamily: boolean;
  onNavigate: (href: string) => void;
}) {
  const items = isResident
    ? [
        { href: '/me/home', label: 'My home' },
        { href: '/me/credentials', label: 'My keys' },
        { href: '/me/events', label: 'My activity' },
        ...(isMultiFamily ? [{ href: '/me/amenities/reservations', label: 'Amenity bookings' }] : []),
      ]
    : [
        ...(isMultiFamily ? [] : [{ href: '/me/workplace', label: 'My workplace' }]),
        { href: '/me/credentials', label: 'My keys' },
        { href: '/me/events', label: 'My activity' },
      ];

  return (
    <details className="relative">
      <summary className="flex cursor-pointer list-none items-center gap-1.5 rounded-full px-2 py-1 font-body-md text-body-md text-on-surface-variant hover:bg-surface-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
        <Icon name="account_circle" className="text-xl" aria-hidden="true" />
        <span className="hidden max-w-[16rem] truncate sm:inline">{email}</span>
        <Icon name="expand_more" className="text-base" aria-hidden="true" />
      </summary>
      <div className="absolute right-0 z-40 mt-1 w-56 overflow-hidden rounded-lg border border-outline-variant bg-surface-container-lowest py-1 shadow-lg">
        <p className="truncate px-4 py-2 font-body-sm text-body-sm text-on-surface-variant sm:hidden">
          {email}
        </p>
        {items.map((i) => (
          <button
            key={i.href}
            type="button"
            onClick={() => onNavigate(i.href)}
            className="block w-full px-4 py-2 text-left font-body-md text-body-md text-on-surface hover:bg-surface-container"
          >
            {i.label}
          </button>
        ))}
        <div className="my-1 border-t border-outline-variant sm:hidden" />
        <div className="flex items-center justify-between gap-2 px-4 py-2 sm:hidden">
          <span className="font-body-sm text-body-sm text-on-surface-variant">Theme</span>
          <ThemeToggle />
        </div>
        <div className="my-1 border-t border-outline-variant" />
        <button
          type="button"
          onClick={() => onNavigate('/account/security')}
          className="block w-full px-4 py-2 text-left font-body-md text-body-md text-on-surface hover:bg-surface-container"
        >
          Account security
        </button>
      </div>
    </details>
  );
}

/**
 * P0-1: Operator-actionable banner shown in the shell when
 * `/auth/me.default_tenant_health` is anything other than `ok` on a
 * single_tenant or multi_family install. Severity follows the field:
 *
 *   - missing  → destructive (red). Pinned default tenant row is gone.
 *   - inactive → amber. Pinned default tenant is suspended/archived.
 *   - unset    → low-severity info (master-only). Legacy install with
 *                no pinned default; the API silently uses
 *                "first active tenant" as a fallback.
 */
function DefaultTenantHealthBanner({
  health,
  isMaster,
  onRepair,
}: {
  health: 'missing' | 'inactive' | 'unset' | 'ok';
  isMaster: boolean;
  onRepair: () => void;
}) {
  if (health === 'ok') return null;
  const tone =
    health === 'missing'
      ? 'bg-error-container text-on-error-container'
      : health === 'inactive'
        ? 'bg-tertiary-fixed text-on-tertiary-fixed'
        : 'bg-surface-container-high text-on-surface-variant';
  const icon = health === 'missing' ? 'error' : health === 'inactive' ? 'warning' : 'info';
  const label =
    health === 'missing'
      ? 'Default tenant missing — repair required'
      : health === 'inactive'
        ? 'Default tenant inactive — reactivate or repin'
        : 'Legacy install fallback — no default tenant pinned';
  const detail =
    health === 'missing'
      ? 'The pinned default tenant no longer exists. Tenant-scoped pages will fail until a master admin picks a new default.'
      : health === 'inactive'
        ? 'The pinned default tenant is suspended or archived. Reactivate it or repin to a different tenant.'
        : 'No default tenant is pinned. The API is falling back to the first active tenant — pin one explicitly to avoid surprises.';
  return (
    <div
      role={health === 'unset' ? 'status' : 'alert'}
      className={`${tone} px-margin-mobile md:px-margin-desktop py-2 font-body-md text-body-md flex items-center gap-2`}
    >
      <Icon name={icon} />
      <span className="flex-1">
        <strong className="font-label-md text-label-md uppercase">{label}</strong>
        <span className="block sm:inline sm:ml-2">{detail}</span>
      </span>
      {isMaster ? (
        <button
          type="button"
          onClick={onRepair}
          className="underline font-medium whitespace-nowrap"
        >
          Open default-tenant settings
        </button>
      ) : null}
    </div>
  );
}
